import { useMemo } from "react";
import { useContentGaps } from "../api/queries";
import { Card, Empty, ErrorBanner, Loading, PageHeader, Td, Th } from "../components/ui";
import { DonutChart, HorizontalBarChart, type BarDatum, type Slice } from "../components/charts";

// Palette for reason-code slices; mirrors the tones used elsewhere in the app.
const REASON_COLORS = ["#2767D5", "#1E9E5A", "#f59e0b", "#dc2626", "#8b5cf6", "#0ea5e9", "#9ca3af"];

export function ContentGapsPage() {
  const gaps = useContentGaps();

  // Refusals grouped by reason code (null -> "unspecified"), largest first.
  const reasonSlices: Slice[] = useMemo(() => {
    const byReason = new Map<string, number>();
    for (const r of gaps.data?.recurring_refusals ?? []) {
      const key = r.reason_code ?? "unspecified";
      byReason.set(key, (byReason.get(key) ?? 0) + r.count);
    }
    return [...byReason.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([name, value], i) => ({ name, value, color: REASON_COLORS[i % REASON_COLORS.length] }));
  }, [gaps.data]);

  // Times each question was refused, most-asked first.
  const questionBars: BarDatum[] = useMemo(() => {
    return [...(gaps.data?.recurring_refusals ?? [])]
      .sort((a, b) => b.count - a.count)
      .map((r) => ({ name: r.question, value: r.count, color: "#2767D5", fullLabel: r.question }));
  }, [gaps.data]);

  const totalRefusals = reasonSlices.reduce((sum, s) => sum + s.value, 0);

  return (
    <div className="p-6">
      <PageHeader
        title="Content gaps"
        subtitle="Questions the tutor keeps refusing, and answers learners rated low. These point to content missing from Moodle or not yet approved."
      />
      <ErrorBanner error={gaps.error} />
      {gaps.isLoading ? <Loading /> : gaps.data && (
        <div className="space-y-4">
          <div className="grid gap-4 min-[1000px]:grid-cols-3">
            <Card title="Refusals by reason">
              {!reasonSlices.length ? <Empty>No recurring refusals.</Empty> : (
                <DonutChart
                  data={reasonSlices}
                  centerValue={totalRefusals.toLocaleString()}
                  centerLabel="refusals"
                />
              )}
            </Card>
            <Card className="min-[1000px]:col-span-2" title="Top recurring questions">
              {!questionBars.length ? <Empty>No recurring refusals.</Empty> : (
                <HorizontalBarChart data={questionBars} />
              )}
            </Card>
          </div>
          <Card title="Recurring unanswered questions">
            {!gaps.data.recurring_refusals.length ? <Empty>No recurring refusals.</Empty> : (
              <table className="w-full">
                <thead className="border-b"><tr><Th>Question</Th><Th>Reason</Th><Th className="text-right">Times asked</Th></tr></thead>
                <tbody className="divide-y">
                  {gaps.data.recurring_refusals.map((r, i) => (
                    <tr key={i}>
                      <Td>{r.question}</Td>
                      <Td><code className="text-xs">{r.reason_code ?? "—"}</code></Td>
                      <Td className="text-right tabular-nums">{r.count}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
          <Card title="Low-rated answers">
            {!gaps.data.low_rated_answers.length ? <Empty>No low-rated answers.</Empty> : (
              <table className="w-full">
                <thead className="border-b"><tr><Th>Question</Th><Th>Answer</Th><Th className="text-right">Avg rating</Th><Th className="text-right">Ratings</Th></tr></thead>
                <tbody className="divide-y">
                  {gaps.data.low_rated_answers.map((a) => (
                    <tr key={a.interaction_id}>
                      <Td className="align-top">{a.question}</Td>
                      <Td className="max-w-lg align-top text-gray-600"><div className="line-clamp-3" title={a.answer}>{a.answer}</div></Td>
                      <Td className="text-right align-top tabular-nums">{a.average_rating}</Td>
                      <Td className="text-right align-top tabular-nums">{a.rating_count}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}
